import type {
  HeartbeepConfig,
  EventType,
  TrackEventData,
  TrackEventPayload,
} from "./types";

export * from "./types";

export class Heartbeep {
  private config: HeartbeepConfig;
  private queue: Array<{ event: EventType; payload?: TrackEventPayload }> = [];
  private isReady = false;

  constructor(config: HeartbeepConfig) {
    if (!config.siteId) {
      throw new Error("[Heartbeep] siteId is required");
    }

    // Build the API URL
    let apiUrl = config.apiUrl || 'https://heartbeep.live/api/beep/website';
    // Remove trailing slash
    apiUrl = apiUrl.replace(/\/$/, '');
    // Append siteId if not already included
    if (!apiUrl.includes(config.siteId)) {
      apiUrl = `${apiUrl}/${config.siteId}`;
    }

    this.config = {
      ...config,
      apiUrl,
      debug: config.debug || false,
    };

    // Initialize when DOM is ready
    if (typeof window !== "undefined") {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.init());
      } else {
        this.init();
      }
    }
  }

  private init(): void {
    this.isReady = true;
    this.processQueue();

    if (this.config.debug) {
      console.log("[Heartbeep] Initialized with siteId:", this.config.siteId);
    }
  }

  private processQueue(): void {
    while (this.queue.length > 0) {
      const item = this.queue.shift();
      if (item) {
        this.track(item.event, item.payload);
      }
    }
  }

  private async send(data: TrackEventData): Promise<void> {
    if (this.config.debug) {
      console.log("[Heartbeep] Tracking event:", data);
    }

    try {
      const payload = JSON.stringify(data);

      // Try sendBeacon first for reliability
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const sent = navigator.sendBeacon(this.config.apiUrl!, payload);
        if (sent) return;
      }

      // Fallback to fetch
      if (typeof fetch !== "undefined") {
        await fetch(this.config.apiUrl!, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        });
      }
    } catch (error) {
      if (this.config.debug) {
        console.error("[Heartbeep] Error tracking event:", error);
      }
    }
  }

  track(event: EventType, payload?: TrackEventPayload): void {
    // Queue events if not ready
    if (!this.isReady) {
      this.queue.push({ event, payload });
      return;
    }

    // Skip tracking if running on server
    if (typeof window === "undefined") {
      return;
    }

    const data: TrackEventData = {
      event,
      name: payload?.name || document.title,
      url: window.location.href,
      timestamp: Date.now(),
      metadata: payload?.metadata,
    };

    this.send(data);
  }

  // Convenience method for pageview tracking
  pageview(name?: string): void {
    this.track("pageview", { name });
  }

  // Convenience method for click tracking
  click(name: string, metadata?: Record<string, any>): void {
    this.track("click", { name, metadata });
  }

  // Convenience method for purchase tracking
  purchase(name: string, metadata?: Record<string, any>): void {
    this.track("purchase", { name, metadata });
  }
}

// Convenience beep function for quick tracking
export function beep(event: EventType, name?: string): void {
  console.log(`[Beeper] 🔔 ${event}: ${name || "unnamed event"}`);
}
